import React, { useEffect, useState, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

/** Same roster source as DocumentLanding: members live in memberDetails subcollections, not on team docs. */
async function fetchTeamsWithMemberIds(projectId) {
  const teamsSnap = await getDocs(collection(db, `projects/${projectId}/teams`));
  const teamsData = [];
  for (const teamDoc of teamsSnap.docs) {
    const team = { id: teamDoc.id, ...teamDoc.data(), members: [] };
    const membersSnap = await getDocs(
      collection(db, `projects/${projectId}/teams/${team.id}/memberDetails`)
    );
    for (const memberDoc of membersSnap.docs) {
      team.members.push({
        id: memberDoc.id,
        name: memberDoc.data()?.name || 'Unknown',
      });
    }
    teamsData.push(team);
  }
  return teamsData;
}

const DocumentsPreview = ({ projectId, onClick }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [project, setProject] = useState(null);
  const latestMeta = useRef({ teams: [], project: null });
  const rawDocsRef = useRef([]);
  latestMeta.current = { teams, project };

  // Fetch teams and project for team sign-off progress
  useEffect(() => {
    if (!projectId) return;
    const loadMeta = async () => {
      try {
        const projectSnap = await getDoc(doc(db, 'projects', projectId));
        if (projectSnap.exists()) setProject({ id: projectSnap.id, ...projectSnap.data() });
        const teamsList = await fetchTeamsWithMemberIds(projectId);
        setTeams(teamsList);
      } catch (e) {
        console.error('DocumentsPreview: failed to load project/teams', e);
      }
    };
    loadMeta();
  }, [projectId]);

  // Reprocess when teams/project load (team sign-off needs these for total before first sign)
  const processWithMeta = (docSnapshots) => {
    if (!docSnapshots.length) return [];
    const { teams: teamsList, project: projectData } = latestMeta.current;
    return Promise.all(docSnapshots.map(async (docSnapshot) => {
      const data = typeof docSnapshot.data === 'function' ? docSnapshot.data() : docSnapshot.data || {};
      if (data.status === 'archived') return null;
      let signingProgress = data.signingProgress || 0;
      let completedSigners = data.completedSigners || 0;
      let totalSigners = data.totalSigners || 0;
      // Team sign-off first: some legacy docs still carry docusealSubmissionId; must not use DocuSeal math.
      if (data.mode === 'team_sign_off') {
        const acks = data.acknowledgements || {};
        const memberIds = new Set();
        const relevantTeams = data.teamAssignment === 'all'
          ? teamsList
          : teamsList.filter(t => (data.selectedTeams || []).includes(t.id));
        for (const team of relevantTeams) {
          for (const m of team.members || []) {
            if (m?.id) memberIds.add(m.id);
          }
        }
        if (projectData) {
          if (projectData.createdBy) memberIds.add(projectData.createdBy);
          for (const [userId, d] of Object.entries(projectData.collaborators || {})) {
            if (d?.status === 'accepted' && d?.role === 'pm_collaborator') memberIds.add(userId);
          }
        }
        totalSigners = memberIds.size;
        let signed = 0;
        for (const id of memberIds) {
          if (acks[id]) signed += 1;
        }
        completedSigners = signed;
        signingProgress = totalSigners > 0 ? Math.round((signed / totalSigners) * 100) / 100 : 0;
      } else if (data.docusealSubmissionId) {
        // DocuSeal sunset: use Firestore data only - no API calls
        const submitters = data.docusealSubmitters || [];
        const completedCount = submitters.filter(s => s.status === 'completed' || s.status === 'signed').length;
        const totalCount = submitters.length || (data.recipients?.length || 0);
        const actualTotal = totalCount > 0 ? totalCount : (data.recipients?.length || 0);
        signingProgress = actualTotal > 0 ? Math.round((completedCount / actualTotal) * 100) / 100 : (data.signingProgress || 0);
        completedSigners = completedCount || data.completedSigners || 0;
        totalSigners = actualTotal || data.totalSigners || 0;
      }
      const subtitle = data.mode === 'team_sign_off'
        ? (data.status === 'fully_signed' ? 'Fully signed' : data.status === 'completed' ? 'Completed' : null)
        : (data.status === 'completed' ? 'Completed' : data.status === 'sent' ? 'Sent for signing' : data.status === 'draft' ? 'Draft' : data.status);
      return {
        id: docSnapshot.id,
        type: 'document',
        title: data.name,
        subtitle: subtitle || (data.mode === 'team_sign_off' ? 'In progress' : data.status),
        timestamp: data.uploadedAt && (data.uploadedAt.toDate ? data.uploadedAt.toDate() : new Date(data.uploadedAt)),
        progress: signingProgress,
        completedSigners,
        totalSigners,
        status: data.status,
        docusealSubmissionId: data.docusealSubmissionId || null,
        mode: data.mode,
      };
    })).then(results => results.filter(Boolean));
  };

  // Reprocess documents when teams/project load (fixes team sign-off total before first sign)
  useEffect(() => {
    if (rawDocsRef.current.length === 0 || !projectId) return;
    processWithMeta(rawDocsRef.current).then(docsData => {
          const sorted = docsData.sort((a, b) => {
        const getGroup = (doc) => {
          if (doc.mode === 'general_record' || doc.status === 'record') return 2; // general docs last
          if (doc.status === 'fully_signed' || doc.status === 'completed') return 1; // fully signed
          return 0; // pending signatures first
        };
        const ga = getGroup(a), gb = getGroup(b);
        if (ga !== gb) return ga - gb;
        return (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0);
      });
      setDocuments(sorted);
    });
  }, [teams, project, projectId]);

  useEffect(() => {
    if (!projectId) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, `projects/${projectId}/documents`),
        orderBy('uploadedAt', 'desc')
      ),
      async (snapshot) => {
        if (!snapshot.empty) {
          rawDocsRef.current = snapshot.docs;
          const docsData = await processWithMeta(snapshot.docs);
          const sortedDocs = docsData.sort((a, b) => {
            const getGroup = (doc) => {
              if (doc.mode === 'general_record' || doc.status === 'record') return 2; // general docs last
              if (doc.status === 'fully_signed' || doc.status === 'completed') return 1; // fully signed
              return 0; // pending signatures first
            };
            const ga = getGroup(a), gb = getGroup(b);
            if (ga !== gb) return ga - gb;
            return (b.timestamp?.getTime() || 0) - (a.timestamp?.getTime() || 0);
          });
          setDocuments(sortedDocs);
        } else {
          rawDocsRef.current = [];
          setDocuments([]);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching documents:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        Loading...
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div style={{
        height: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--branding-app-surface-panel-hint)',
        fontSize: '14px'
      }}>
        No recent activity
      </div>
    );
  }

  return (
    <div 
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        background: 'transparent',
        padding: '16px',
        transition: 'all 0.2s ease'
      }}
    >
      <div style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        paddingRight: '8px'
      }}>
        {documents.map((document, index) => (
          <div 
            key={document.id}
            style={{ 
              display: 'flex', 
              alignItems: 'flex-start', 
              gap: '12px',
              marginBottom: index < documents.length - 1 ? '16px' : '0',
              paddingBottom: index < documents.length - 1 ? '16px' : '0',
              borderBottom: index < documents.length - 1 ? '1px solid var(--branding-app-surface)' : 'none'
            }}
          >
            {/* Icon */}
            <div style={{
              width: '32px',
              height: '32px',
              background: '#f3e8ff',
              border: '1px solid #c4b5fd',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '10px',
              fontWeight: '700',
              color: '#7c3aed',
              flexShrink: 0
            }}>
              DOC
            </div>
            
            {/* Content */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--branding-app-surface-panel-fg)',
                marginBottom: '4px',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {document.title}
              </div>
              
              <div style={{
                fontSize: '13px',
                color: 'var(--branding-app-surface-panel-sub)',
                marginBottom: '8px',
                lineHeight: '1.3',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {document.subtitle}
              </div>
              
              {/* Progress bar for DocuSeal and Team sign-off documents */}
              {((document.docusealSubmissionId && document.status !== 'completed') ||
                (document.mode === 'team_sign_off' && document.totalSigners > 0)) && (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '4px'
                  }}>
                    <span style={{ fontSize: '11px', color: 'var(--branding-app-surface-panel-muted)' }}>
                      {document.completedSigners || 0} of {document.totalSigners || 0} signed
                    </span>
                    <span style={{ fontSize: '11px', color: 'var(--branding-app-surface-panel-muted)' }}>
                      {document.progress ? Math.round(document.progress * 100) : 0}%
                    </span>
                  </div>
                  <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: 'var(--branding-app-surface-panel-border)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${document.progress ? Math.round(document.progress * 100) : 0}%`,
                      height: '100%',
                      backgroundColor: document.status === 'fully_signed' ? '#10b981' : 'var(--branding-app-primary)',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
              
              <div style={{
                fontSize: '12px',
                color: 'var(--branding-app-surface-panel-hint)'
              }}>
                {getTimeAgo(document.timestamp)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentsPreview;
